import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Papa from 'papaparse'
import {
  Alert,
  Button,
  Divider,
  message,
  Modal,
  Radio,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd'
import { Tag as CoreTag } from '@surecompanies/core_components'
import CopyOutlined from '@ant-design/icons/CopyOutlined'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import UploadOutlined from '@ant-design/icons/UploadOutlined'
import { copyToClipboard } from 'components/CopyLink/CopyText'

import {
  getVcnProviderTagColor,
  getVcnProviderTagStyle,
} from './VcnManagementTableCellTags'

import {
  executeVcnBulkActions,
  isVcnBulkExecuteConflictError,
} from '../../api/vcn-bulk-execute.api'
import { previewVcnBulkUpload } from '../../api/vcn-bulk-upload-preview.api'
import type {
  VcnBulkExecuteItem,
  VcnBulkExecuteResponse,
} from '../../api/dto/vcn-bulk-execute.dto'
import {
  VcnManagementAction,
  type VcnBulkUploadPreviewItem,
  type VcnBulkUploadPreviewResponse,
} from '../../api/dto/vcn-bulk-upload-preview.dto'
import { VirtualCardType } from 'modules/virtual-cards/constants'
import {
  describeDisabledProviders,
  useVcnProviderAvailability,
} from 'modules/virtual-cards/useVcnProviderAvailability'
import {
  countRowsOnDisabledProviders,
  dedupeItems,
  middleEllipsis,
  parseCsvRows,
} from './VcnManagementBulkUploadModal.helpers'

const { Text, Paragraph } = Typography

const MAX_ROWS = 5_000

const TEMPLATE_CSV = [
  'benefitId,action,provider',
  `benefit-uuid-example-001,${VcnManagementAction.TERMINATE},`,
  `benefit-uuid-example-002,${VcnManagementAction.TERMINATE_AND_NEW},WEX`,
  `benefit-uuid-example-003,${VcnManagementAction.CREATE_NEW},MARQETA`,
].join('\n')

const columns = [
  {
    title: 'Benefit ID (upload)',
    dataIndex: 'benefitId',
    key: 'benefitId',
    width: 170,
    fixed: 'left' as const,
    render: (v: string) => (
      <span
        title={v}
        style={{
          cursor: 'pointer',
          color: '#003146',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
        onClick={() => {
          void copyToClipboard(v).then((ok) => {
            if (ok) void message.success('Copied to clipboard')
          })
        }}
      >
        {middleEllipsis(v)}
        <CopyOutlined style={{ fontSize: 11 }} />
      </span>
    ),
  },
  {
    title: 'Status',
    key: 'found',
    width: 110,
    fixed: 'left' as const,
    render: (_: unknown, row: VcnBulkUploadPreviewResponse['rows'][0]) =>
      row.found ? (
        <Tag color="success">Matched</Tag>
      ) : (
        <Tag color="default">Not found</Tag>
      ),
  },
  {
    title: 'VCN token',
    dataIndex: 'vcnToken',
    key: 'vcnToken',
    ellipsis: true,
    render: (v: string | undefined) => v || '—',
  },
  {
    title: 'Last 4',
    dataIndex: 'lastFour',
    key: 'lastFour',
    width: 90,
    render: (v: string | undefined) => v || '—',
  },
  {
    title: 'VCN provider',
    dataIndex: 'vcnProvider',
    key: 'vcnProvider',
    width: 120,
    render: (v: string | undefined) => {
      const raw = String(v ?? '').trim()
      if (!raw) return '—'
      return (
        <CoreTag
          color={getVcnProviderTagColor(raw)}
          style={getVcnProviderTagStyle(raw)}
        >
          {raw}
        </CoreTag>
      )
    },
  },
  {
    title: 'Requested action',
    dataIndex: 'requestedAction',
    key: 'requestedAction',
    width: 200,
    render: (v: VcnManagementAction) => {
      if (v === VcnManagementAction.TERMINATE_AND_NEW)
        return <Text>Terminate and new VCN card</Text>
      if (v === VcnManagementAction.CREATE_NEW)
        return <Text>Create new VCN card</Text>
      return <Text>Terminate</Text>
    },
  },
  {
    title: 'Requested provider',
    dataIndex: 'requestedProvider',
    key: 'requestedProvider',
    width: 140,
    render: (v: string | undefined) => {
      const raw = String(v ?? '').trim()
      if (!raw) return '—'
      return (
        <CoreTag
          color={getVcnProviderTagColor(raw)}
          style={getVcnProviderTagStyle(raw)}
        >
          {raw}
        </CoreTag>
      )
    },
  },
]

function downloadTemplateCsv() {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'vcn-bulk-actions-template.csv'
  a.click()
  window.setTimeout(() => {
    URL.revokeObjectURL(url)
  }, 2_000)
}

function downloadUnmatchedCsv(
  rows: VcnBulkUploadPreviewResponse['rows']
): void {
  const lines = [
    'benefitId,action,provider',
    ...rows
      .filter((r) => !r.found)
      .map(
        (r) =>
          `${r.benefitId},${r.requestedAction},${r.requestedProvider ?? ''}`
      ),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'vcn-bulk-unmatched.csv'
  a.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 2_000)
}

export interface VcnManagementBulkUploadModalProps {
  open: boolean
  onClose: () => void
  /** Fired after execute accepts work or 409 conflict — parent opens progress. */
  onJobStarted?: () => void
}

type ModalPhase = 'idle' | 'ready' | 'preview_loading' | 'results'

export function VcnManagementBulkUploadModal({
  open,
  onClose,
  onJobStarted,
}: VcnManagementBulkUploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const previewRequestIdRef = useRef(0)
  const providerAvailability = useVcnProviderAvailability()
  const [phase, setPhase] = useState<ModalPhase>('idle')
  const [parseError, setParseError] = useState<string | null>(null)
  const [parseWarning, setParseWarning] = useState<string | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)
  const [uploadCount, setUploadCount] = useState(0)
  const [pendingItems, setPendingItems] = useState<
    VcnBulkUploadPreviewItem[] | null
  >(null)
  const [preview, setPreview] = useState<VcnBulkUploadPreviewResponse | null>(
    null
  )
  const [isExecuteLoading, setIsExecuteLoading] = useState(false)
  const [executeError, setExecuteError] = useState<string | null>(null)
  const [executeSuccess, setExecuteSuccess] =
    useState<VcnBulkExecuteResponse | null>(null)
  const [rowFilter, setRowFilter] = useState<'all' | 'matched' | 'unmatched'>(
    'all'
  )

  const reset = useCallback(() => {
    previewRequestIdRef.current++
    setPhase('idle')
    setParseError(null)
    setParseWarning(null)
    setApiError(null)
    setUploadCount(0)
    setPendingItems(null)
    setPreview(null)
    setIsExecuteLoading(false)
    setExecuteError(null)
    setExecuteSuccess(null)
    setRowFilter('all')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  useEffect(() => {
    if (!open) reset()
  }, [open, reset])

  const runPreview = useCallback(async (items: VcnBulkUploadPreviewItem[]) => {
    const requestId = ++previewRequestIdRef.current
    setPhase('preview_loading')
    setApiError(null)
    setExecuteError(null)
    setExecuteSuccess(null)
    try {
      const res = await previewVcnBulkUpload({ items })
      if (requestId !== previewRequestIdRef.current) return
      setPreview(res)
      setPhase('results')
    } catch (e) {
      if (requestId !== previewRequestIdRef.current) return
      setApiError(e instanceof Error ? e.message : 'Preview request failed')
      setPhase('ready')
    }
  }, [])

  const handlePreview = useCallback(() => {
    if (!pendingItems?.length) return
    void runPreview(pendingItems)
  }, [pendingItems, runPreview])

  const matchedRows = useMemo(() => {
    if (!preview) return []
    return preview.rows.filter((r) => {
      if (!r.found) return false
      if (r.requestedAction === VcnManagementAction.CREATE_NEW) return true
      return Boolean(r.vcnToken)
    })
  }, [preview])

  const executePayload = useMemo((): VcnBulkExecuteItem[] => {
    return matchedRows.map((r) => ({
      benefit_id: r.benefitId,
      vcn_token:
        r.requestedAction === VcnManagementAction.CREATE_NEW
          ? undefined
          : r.vcnToken,
      action: r.requestedAction,
      provider: r.requestedProvider,
    }))
  }, [matchedRows])

  const marqetaRowCount = useMemo(
    () =>
      matchedRows.filter(
        (r) =>
          r.requestedProvider === VirtualCardType.MARQETA &&
          r.requestedAction !== VcnManagementAction.TERMINATE
      ).length,
    [matchedRows]
  )

  // The CSV was gated at upload, but a switch can flip while the preview sits
  // on screen — re-check before the rows leave the browser (EMS-9720).
  const blockedRowCount = useMemo(
    () => countRowsOnDisabledProviders(matchedRows, providerAvailability),
    [matchedRows, providerAvailability]
  )

  const handleRun = useCallback(async () => {
    if (executePayload.length === 0) return
    if (blockedRowCount > 0) {
      setExecuteError(
        `${describeDisabledProviders(providerAvailability.disabledProviders)}. ${blockedRowCount} matched row${blockedRowCount === 1 ? '' : 's'} would create a card there — upload again once it is back.`
      )
      return
    }
    setIsExecuteLoading(true)
    setExecuteError(null)
    try {
      const res = await executeVcnBulkActions({ items: executePayload })
      setExecuteSuccess(res)
      onJobStarted?.()
    } catch (e) {
      if (isVcnBulkExecuteConflictError(e)) {
        void message.warning(
          e.message ||
            'A bulk job is already queued or running — opening job progress.'
        )
        onJobStarted?.()
        return
      }
      setExecuteError(
        e instanceof Error ? e.message : 'Bulk execute request failed'
      )
    } finally {
      setIsExecuteLoading(false)
    }
  }, [executePayload, onJobStarted, blockedRowCount, providerAvailability])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return

      setParseError(null)
      setParseWarning(null)
      setPreview(null)
      setPendingItems(null)
      setApiError(null)
      setExecuteError(null)
      setExecuteSuccess(null)
      setPhase('idle')

      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          if (result.errors.length > 0) {
            setParseError(
              result.errors.map((err) => err.message).join('; ') ||
                'CSV parse error'
            )
            return
          }

          const {
            items,
            badActions,
            badProviders,
            disabledProviders: disabledProviderRows,
            missingBenefitIdCount,
          } = parseCsvRows(result.data, providerAvailability)

          if (badActions.length > 0) {
            setParseError(
              `Invalid or missing action on row(s): ${badActions.slice(0, 15).join(', ')}${badActions.length > 15 ? '…' : ''}. Use "${VcnManagementAction.TERMINATE}", "${VcnManagementAction.TERMINATE_AND_NEW}", or "${VcnManagementAction.CREATE_NEW}".`
            )
            return
          }

          if (badProviders.length > 0) {
            setParseError(
              `Invalid provider on row(s): ${badProviders.slice(0, 15).join(', ')}${badProviders.length > 15 ? '…' : ''}. Use "${VirtualCardType.WEX}", "${VirtualCardType.MARQETA}", or leave blank for ${VirtualCardType.WEX}.`
            )
            return
          }

          if (disabledProviderRows.length > 0) {
            const { enabledProviders, disabledProviders } = providerAvailability
            const rowList = `${disabledProviderRows.slice(0, 15).join(', ')}${disabledProviderRows.length > 15 ? '…' : ''}`
            setParseError(
              `${describeDisabledProviders(disabledProviders)}. Row(s) ${rowList} ask for a new card on ${disabledProviders.length > 1 ? 'those providers' : 'it'}. ${
                enabledProviders.length > 0
                  ? `Switch those rows to ${enabledProviders.join(' or ')}, or remove them. A blank provider means ${VirtualCardType.WEX}.`
                  : 'Card creation is paused — remove those rows and try again later.'
              }`
            )
            return
          }

          const {
            deduped,
            actionConflictCount,
            providerConflictCount,
            sameDupCount,
          } = dedupeItems(items)

          const warnings: string[] = []
          if (missingBenefitIdCount > 0)
            warnings.push(
              `${missingBenefitIdCount} row${missingBenefitIdCount === 1 ? '' : 's'} skipped — no benefitId found.`
            )
          if (actionConflictCount > 0)
            warnings.push(
              `${actionConflictCount} row${actionConflictCount === 1 ? '' : 's'} had a conflicting action for a duplicate benefitId — the first occurrence was kept.`
            )
          if (providerConflictCount > 0)
            warnings.push(
              `${providerConflictCount} row${providerConflictCount === 1 ? '' : 's'} requested a different provider for a duplicate benefitId — the first occurrence was kept, so check the Requested provider column below.`
            )
          if (sameDupCount > 0)
            warnings.push(
              `${sameDupCount} exact duplicate${sameDupCount === 1 ? '' : 's'} removed.`
            )
          setParseWarning(warnings.length > 0 ? warnings.join(' ') : null)

          if (deduped.length === 0) {
            setParseError(
              'No data rows with benefitId and a valid action were found.'
            )
            return
          }

          if (deduped.length > MAX_ROWS) {
            setParseError(
              `File has ${deduped.length} rows; maximum is ${MAX_ROWS}.`
            )
            return
          }

          setUploadCount(deduped.length)
          setPendingItems(deduped)
          void runPreview(deduped)
        },
        error: (err) => {
          setParseError(err.message || 'Failed to read file')
        },
      })
    },
    [runPreview, providerAvailability]
  )

  const matchedTerminate = useMemo(() => {
    if (!preview) return 0
    return preview.rows.filter(
      (r) =>
        r.found &&
        r.vcnToken &&
        r.requestedAction === VcnManagementAction.TERMINATE
    ).length
  }, [preview])

  const matchedTerminateAndNew = useMemo(() => {
    if (!preview) return 0
    return preview.rows.filter(
      (r) =>
        r.found &&
        r.vcnToken &&
        r.requestedAction === VcnManagementAction.TERMINATE_AND_NEW
    ).length
  }, [preview])

  const matchedCreateNew = useMemo(() => {
    if (!preview) return 0
    return preview.rows.filter(
      (r) => r.found && r.requestedAction === VcnManagementAction.CREATE_NEW
    ).length
  }, [preview])

  const unmatchedCount = preview ? uploadCount - preview.matchedCount : 0

  const filteredRows = useMemo(() => {
    if (!preview) return []
    if (rowFilter === 'matched') return preview.rows.filter((r) => r.found)
    if (rowFilter === 'unmatched') return preview.rows.filter((r) => !r.found)
    return preview.rows
  }, [preview, rowFilter])

  return (
    <Modal
      title="Bulk VCN actions (upload)"
      open={open}
      onCancel={onClose}
      footer={
        phase === 'results' ? (
          <Space wrap>
            {!executeSuccess ? (
              <Button
                type="primary"
                loading={isExecuteLoading}
                disabled={executePayload.length === 0 || blockedRowCount > 0}
                onClick={() => void handleRun()}
              >
                Run
              </Button>
            ) : null}
            <Button onClick={reset}>Upload another file</Button>
            <Button
              type={executeSuccess ? 'primary' : 'default'}
              onClick={onClose}
            >
              Close
            </Button>
          </Space>
        ) : phase === 'ready' || phase === 'preview_loading' ? (
          <Space>
            <Button onClick={onClose} disabled={phase === 'preview_loading'}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={phase === 'preview_loading'}
              disabled={!pendingItems?.length}
              onClick={handlePreview}
            >
              Retry Preview
            </Button>
          </Space>
        ) : (
          <Button onClick={onClose}>Cancel</Button>
        )
      }
      width={920}
      destroyOnClose
    >
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Upload a CSV with <strong>benefitId</strong> and <strong>action</strong>
        . Each benefit will be resolved to a VCN (WEX or Marqeta) automatically.
        Click <strong>Run</strong> to queue bulk termination for matched rows.{' '}
        Max {MAX_ROWS.toLocaleString()} rows per file.
      </Paragraph>

      {phase !== 'results' && (
        <Space wrap style={{ marginBottom: 16 }}>
          <Button
            icon={<DownloadOutlined />}
            onClick={downloadTemplateCsv}
            disabled={phase === 'preview_loading'}
          >
            Download template
          </Button>
          <Button
            type={phase === 'ready' ? 'default' : 'primary'}
            icon={<UploadOutlined />}
            disabled={phase === 'preview_loading'}
            onClick={() => fileInputRef.current?.click()}
          >
            {phase === 'ready' ? 'Replace CSV' : 'Upload CSV'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Space>
      )}

      {parseError && (
        <Alert type="error" message={parseError} style={{ marginBottom: 12 }} />
      )}
      {parseWarning && (
        <Alert
          type="warning"
          message={parseWarning}
          style={{ marginBottom: 12 }}
        />
      )}
      {apiError && (
        <Alert type="error" message={apiError} style={{ marginBottom: 12 }} />
      )}
      {executeError && (
        <Alert
          type="error"
          message={executeError}
          style={{ marginBottom: 12 }}
        />
      )}

      {phase === 'ready' && pendingItems && apiError && (
        <Alert
          type="warning"
          showIcon
          message={`${pendingItems.length} row${pendingItems.length === 1 ? '' : 's'} parsed — preview failed`}
          description="Click Retry Preview to try again."
          style={{ marginBottom: 12 }}
        />
      )}

      {phase === 'preview_loading' && (
        <Alert type="info" message="Running preview…" showIcon />
      )}

      {phase === 'results' && preview && (
        <>
          {isExecuteLoading && (
            <Alert
              type="info"
              message="Starting bulk termination job…"
              showIcon
              style={{ marginBottom: 12 }}
            />
          )}
          {executeSuccess && (
            <Alert
              type="success"
              showIcon
              message={`Queued ${executeSuccess.submittedCount} action(s)`}
              description={executeSuccess.message}
              style={{ marginBottom: 12 }}
            />
          )}
          {!executeSuccess && preview && executePayload.length === 0 && (
            <Alert
              type="warning"
              message={
                preview.matchedCount === 0
                  ? 'No rows matched the system — nothing to run.'
                  : 'Preview did not return VCN tokens for matched rows, so Run cannot build a payload.'
              }
              style={{ marginBottom: 12 }}
            />
          )}
          {blockedRowCount > 0 && !executeSuccess && (
            <Alert
              type="error"
              showIcon
              message={`${describeDisabledProviders(providerAvailability.disabledProviders)}.`}
              description={`${blockedRowCount} matched row${blockedRowCount === 1 ? '' : 's'} would create a card there, so Run is disabled. Upload the file again once the provider is back, or remove those rows.`}
              style={{ marginBottom: 12 }}
            />
          )}
          {marqetaRowCount > 0 && !executeSuccess && (
            <Alert
              type="warning"
              showIcon
              message={`${marqetaRowCount} matched row${marqetaRowCount === 1 ? '' : 's'} will create MARQETA card${marqetaRowCount === 1 ? '' : 's'}.`}
              description="WEX is the standard provider. Only continue with Marqeta if the carrier does not accept WEX."
              style={{ marginBottom: 12 }}
            />
          )}
          <Divider orientation="left">Summary</Divider>
          <Space size="large" wrap style={{ marginBottom: 16 }}>
            <Statistic title="Rows in file" value={uploadCount} />
            <Statistic title="Matched in system" value={preview.matchedCount} />
            <Statistic
              title="Not matched"
              value={uploadCount - preview.matchedCount}
            />
          </Space>

          <Divider orientation="left">
            By requested action (matched only)
          </Divider>
          <Space size="large" wrap style={{ marginBottom: 16 }}>
            <Statistic title="Will terminate" value={matchedTerminate} />
            <Statistic
              title="Will terminate and new VCN"
              value={matchedTerminateAndNew}
            />
            <Statistic title="Will create new VCN" value={matchedCreateNew} />
          </Space>

          <Divider orientation="left">Preview rows</Divider>
          <Space
            style={{
              marginBottom: 12,
              width: '100%',
              justifyContent: 'space-between',
            }}
            wrap
          >
            <Radio.Group
              value={rowFilter}
              onChange={(e) => setRowFilter(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
              options={[
                { label: `All (${uploadCount})`, value: 'all' },
                {
                  label: `Matched (${preview.matchedCount})`,
                  value: 'matched',
                },
                { label: `Unmatched (${unmatchedCount})`, value: 'unmatched' },
              ]}
            />
            {unmatchedCount > 0 && (
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => downloadUnmatchedCsv(preview.rows)}
              >
                Export unmatched
              </Button>
            )}
          </Space>
          <Table
            size="small"
            rowKey={(row, index) =>
              `${row.benefitId}-${row.requestedAction}-${index}`
            }
            columns={columns}
            dataSource={filteredRows}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            scroll={{ x: 1020 }}
          />
        </>
      )}
    </Modal>
  )
}
